import React, { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate } from "./Navigation.jsx";
import Footer from "./Footer.jsx";
import { useCurrencySymbol } from "./Currency.jsx";
import { wsRequest, onBroadcast, setDemoRouteToken } from "./wsClient.js";
import { showToast } from "./toast.js";
import { useTheme } from "./ThemeContext.jsx";
import TabBar from "./TabBar.jsx";
import CompetitionReplayModal from "./CompetitionReplayModal.jsx";
import { announceAuthChange } from "./authPrompt.js";
import TotpSetup from "./TotpSetup.jsx";
import PhoneVerificationModal from "./PhoneVerificationModal.jsx";
import { siteDate, siteDateTime } from "./siteTime.js";

const API_BASE = ""; // same-origin — nginx proxies /api and /ws to the backend
const USER_TOKEN_KEY = "589comps_user_token";

const FIELD_DEFS = [
  { key: "username", label: "Username", type: "text" },
  { key: "firstName", label: "First name", type: "text" },
  { key: "lastName", label: "Last name", type: "text" },
  { key: "dob", label: "Date of birth", type: "date" },
  { key: "email", label: "Email address", type: "email" },
  { key: "addressLine1", label: "Address line 1", type: "text" },
  { key: "addressLine2", label: "Address line 2", type: "text" },
  { key: "addressLine3", label: "Address line 3", type: "text" },
  { key: "townCity", label: "Town / city", type: "text" },
  { key: "country", label: "Country", type: "text" },
  { key: "postcode", label: "Postcode", type: "text" },
];

// The fields root confirmed matter for delivering a physical prize —
// deliberately includes dob (a partner-set minimum age to enter could
// make this the final check that actually matters) and all three
// address lines, not just the first. Excludes username/email, which
// FIELD_DEFS also covers but aren't delivery-relevant.
const DELIVERY_PROFILE_FIELDS = [
  "firstName",
  "lastName",
  "addressLine1",
  "addressLine2",
  "addressLine3",
  "townCity",
  "country",
  "postcode",
  "dob",
];
function isDeliveryProfileComplete(profile) {
  return DELIVERY_PROFILE_FIELDS.every((field) => !!profile?.[field]?.trim());
}

const getInputStyle = (theme) => ({
  width: "100%",
  padding: 8,
  marginTop: 4,
  marginBottom: 14,
  background: "var(--bg-input)",
  border: "1px solid var(--border-color-light)",
  borderRadius: 6,
  color: "var(--text-primary)",
  colorScheme: theme, // without this, Chromium/Brave renders native date/color picker icons assuming the opposite theme's background, so they end up invisible/unclickable
});

// /profile/account, /profile/entries, /profile/payments — the URL is the
// single source of truth for which tab shows, same approach as the admin
// dashboard's routes.
function parseProfilePath(path) {
  const segments = (path || "").split("/").filter(Boolean); // ["profile", "entries"]
  return segments[1] || "account";
}

// HOW AN ENTRY WAS PAID FOR, IN THE BUYER'S OWN HISTORY (1.557.0).
//
// This read `wasFree ? "free entry" : "paid entry"` off a flag the server
// had been computing wrongly since 17 Sep 2026 — it was the retired
// allowance flag, always false — so every row said "paid entry", including
// one funded entirely by the claimed free entry. The buyer took the
// no-purchase-necessary route and their own history told them otherwise.
//
// THREE CASES, because a straddle is real: a free grant is one entry's
// worth at CLAIM time, so an operator raising the entry price between the
// claim and the entry leaves the rest to come from the bought balance.
//
// ROWS WRITTEN BEFORE THE FIX carry no figures at all, and are shown the
// way they always were rather than guessed at. `wasFree` is false on all
// of them, so they read "paid entry" — which is what they have always
// read, and is sometimes wrong. Saying "paid entry (not recorded)" on
// every old row would be louder than the thing it is admitting to.
function entryFunding(e) {
  if (e.wasFree) return "free entry";
  const fromFree = Number(e.creditsFromFree) || 0;
  const fromBought = Number(e.creditsFromBought) || 0;
  if (fromFree > 0) {
    return `part-free entry · ${fromFree} free + ${fromBought} credit${fromBought === 1 ? "" : "s"}`;
  }
  if (fromBought > 0) return `paid entry · ${fromBought} credit${fromBought === 1 ? "" : "s"}`;
  return "paid entry";
}

export default function ProfilePage({ path, demoRouteToken = null }) {
  // Registers the tab's preview mode with wsClient so EVERY wsRequest from
  // this page carries it — the same thing App does for the competition
  // page. Without it this page's calls are Live regardless of what mode
  // the visitor is in, which is precisely the inconsistency that made an
  // entry-credit top-up ask for live payment credentials on a test site.
  useEffect(() => {
    setDemoRouteToken(demoRouteToken);
  }, [demoRouteToken]);

  const symbol = useCurrencySymbol();
  const { theme } = useTheme();
  const [token] = useState(() => localStorage.getItem(USER_TOKEN_KEY));
  const navigate = useNavigate();
  const tab = parseProfilePath(path);

  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  // See InstallPage.jsx's identical confirmPasswordRef for the full
  // reasoning — feeds the browser's own native validity state so this
  // integrates with theme.css's red/green border styling the same way
  // a plain required/minLength failure would.
  // THE THREE DOCUMENTS (1.500.0). The registration form links all three
  // and this page showed a bare greyed-out tick with no way to re-read
  // what had been agreed to. Same unauthenticated action AuthForm uses,
  // and the same fallback: no link, plain text, never a dead anchor.
  const [legalDocs, setLegalDocs] = useState([]);
  useEffect(() => {
    wsRequest("legalDocs.list")
      .then((docs) => setLegalDocs(Array.isArray(docs) ? docs : []))
      .catch(() => setLegalDocs([]));
  }, []);
  const legalLink = (key, label) => {
    const doc = legalDocs.find((d) => d.key === key);
    const url = doc?.htmlUrl || doc?.pdfUrl;
    return url ? (
      <a key={key} href={url} target="_blank" rel="noopener noreferrer">
        {label}
      </a>
    ) : (
      <span key={key}>{label}</span>
    );
  };

  const confirmNewPasswordRef = useRef(null);
  useEffect(() => {
    if (confirmNewPasswordRef.current) {
      confirmNewPasswordRef.current.setCustomValidity(
        passwordForm.confirmPassword && passwordForm.confirmPassword !== passwordForm.newPassword ? "Passwords don't match." : ""
      );
    }
  }, [passwordForm.newPassword, passwordForm.confirmPassword]);
  const [passwordBusy, setPasswordBusy] = useState(false);

  // Two-factor authentication — optional for a regular account (unlike
  // admin/root, where it's mandatory and handled entirely within
  // AdminApp.jsx's own interstitial instead). settingUpTotp shows the
  // shared TotpSetup flow in place of the normal on/off toggle; the
  // disable form requires the current password plus a live code, same
  // as the backend's own auth.totp.disable enforces.
  const [settingUpTotp, setSettingUpTotp] = useState(false);
  const [disableTotpForm, setDisableTotpForm] = useState({ password: "", code: "" });
  const [disableTotpBusy, setDisableTotpBusy] = useState(false);

  const [preferredSpend, setPreferredSpend] = useState("");
  const [prefsBusy, setPrefsBusy] = useState(false);

  const [entries, setEntries] = useState(null);
  // WHICH COMPETITIONS CAN ACTUALLY BE WATCHED BACK. One that finished
  // before replays existed, or whose capture failed, has no snapshot —
  // offering a button for it would be a dead end.
  const [replayable, setReplayable] = useState(() => new Set());
  const [replayId, setReplayId] = useState(null);
  const [entriesError, setEntriesError] = useState(null);

  const [detailCompetitionId, setDetailCompetitionId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState(null);

  const [wins, setWins] = useState(null);
  const [winsError, setWinsError] = useState(null);

  const [payoutMethod, setPayoutMethod] = useState("paypal");
  const [payoutDetails, setPayoutDetails] = useState("");
  const [payoutBusy, setPayoutBusy] = useState(false);

  // Off by default until root explicitly turns mandatory phone
  // verification on — the account page only shows anything about it
  // at all once this is true. See requireWinnerPhoneVerified on the
  // backend for what this actually gates (prize payout, not
  // participation).
  const [phoneVerificationEnabled, setPhoneVerificationEnabled] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  // The Account tab's inline phone field is the source of truth for
  // what's currently typed — synced from profile.phone in
  // refreshProfile below, but editable independently until Verify is
  // pressed. Passed as PhoneVerificationModal's initialPhone so the
  // modal can skip straight to sending a code, and reused directly
  // as the Winnings list's own "verify now" prompt too — sharing one
  // draft rather than each entry point keeping its own means
  // something typed but not yet verified on the Account tab is still
  // there if the person switches to Winnings before clicking Verify.
  const [phoneDraft, setPhoneDraft] = useState("");
  // False until phoneVerified is already true AND Reverify has been
  // clicked — before that, the field is already editable just because
  // there's nothing verified yet (see the field's own disabled logic).
  const [phoneUnlocked, setPhoneUnlocked] = useState(false);

  const [credits, setCredits] = useState(null);
  const [paymentGroups, setPaymentGroups] = useState(null);
  const [paymentsError, setPaymentsError] = useState(null);

  useEffect(() => {
    wsRequest("siteSettings.get")
      .then((data) => setPhoneVerificationEnabled(!!data.phoneVerificationEnabled))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (path === "/profile" || path === "/profile/") navigate("/profile/account");
  }, [path]);

  const refreshProfile = () => {
    if (!token) return;
    wsRequest("auth.me", {}, token)
      .then((data) => {
        setProfile(data);
        setForm(data);
        setPreferredSpend(data.defaultSpendAmount || "");
        if (data.payoutMethod) setPayoutMethod(data.payoutMethod);
        if (data.payoutDetails) setPayoutDetails(data.payoutDetails);
        setPhoneDraft(data.phone || "");
        setPhoneUnlocked(false);
      })
      .catch((err) => {
        if (err.status === 401 || err.status === 403) {
          setError("Your session has expired — please log in again from the home page.");
        } else {
          setError("Could not reach backend.");
        }
      });
  };

  useEffect(() => {
    if (!token) return;
    refreshProfile();

    wsRequest("auth.myEntries", {}, token)
      .then((data) => setEntries(data))
      .catch(() => setEntriesError("Could not reach backend."));

    fetch("/api/v1/auth/my-replays", { headers: { "x-auth-token": token } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => setReplayable(new Set((list || []).map((r) => r.competitionId))))
      .catch(() => {});

    loadWins();

    wsRequest("auth.myPayments", {}, token)
      .then((data) => setPaymentGroups(data))
      .catch(() => setPaymentsError("Could not reach backend."));
    // Entry credit. Fails quietly on purpose: a balance is an extra line
    // on this page, not the point of it, and a backend without this action
    // should not blank the account tab.
    wsRequest("auth.myCredits", {}, token)
      .then((data) => setCredits(data))
      .catch(() => setCredits(null));
  }, [token]);

  // Real-time push from the backend the instant a newer login elsewhere
  // supersedes this session (see issueToken/notifySessionSuperseded on the
  // backend). This page's own token is a one-time read with no setter, so
  // it can't clear its own session the way SiteHeader.jsx can — instead,
  // since SiteHeader is already mounted alongside this page and has its
  // own listener for the same broadcast (clearing the session and
  // prompting login), this just needs to navigate away from a page that's
  // now entirely stale/inaccessible, landing somewhere that prompt will
  // actually be visible rather than leaving already-loaded protected
  // content on screen indefinitely.
  useEffect(() => {
    return onBroadcast((message) => {
      if (message.type === "session-superseded") navigate("/");
    });
  }, []);

  const loadWins = () => {
    if (!token) return;
    wsRequest("auth.myWins", {}, token)
      .then((data) => setWins(data))
      .catch(() => setWinsError("Could not reach backend."));
  };

  const openCompetitionDetail = (competitionId) => {
    setDetailCompetitionId(competitionId);
    setDetail(null);
    setDetailError(null);
    wsRequest("auth.myCompetitionDetail", { competitionId }, token)
      .then((data) => setDetail(data))
      .catch((err) => setDetailError(err.message || "Could not reach backend."));
  };

  // Real-time refresh — a "Mark prize paid" or "Request payment details"
  // click happens on an admin's browser, and admin actions in general could
  // come from a different admin session too. Without this, My Winnings would
  // only ever catch up on the next manual page refresh.
  useEffect(() => {
    if (!token) return;
    return onBroadcast((message) => {
      if (message.type === "competitions-changed") loadWins();
    });
  }, [token]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await wsRequest("auth.updateMe", form, token);
      setProfile(data);
      setForm(data);
      announceAuthChange(data, token);
      showToast("Saved.");
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(false);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showToast("New password and confirmation don't match.", "error");
      return;
    }
    setPasswordBusy(true);
    try {
      await wsRequest(
        "auth.changePassword",
        { currentPassword: passwordForm.currentPassword, newPassword: passwordForm.newPassword },
        token
      );
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      showToast("Password changed.");
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setPasswordBusy(false);
  };

  const disableTotp = async (e) => {
    e.preventDefault();
    setDisableTotpBusy(true);
    try {
      await wsRequest("auth.totp.disable", { password: disableTotpForm.password, code: disableTotpForm.code }, token);
      setDisableTotpForm({ password: "", code: "" });
      setProfile((prev) => (prev ? { ...prev, totpEnabled: false } : prev));
      showToast("Two-factor authentication turned off.");
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setDisableTotpBusy(false);
  };

  const savePreferences = async (e) => {
    e.preventDefault();
    setPrefsBusy(true);
    try {
      const data = await wsRequest("auth.updateMe", { defaultSpendAmount: Number(preferredSpend) || 0 }, token);
      setProfile(data);
      setPreferredSpend(data.defaultSpendAmount || "");
      announceAuthChange(data, token);
      showToast("Saved.");
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setPrefsBusy(false);
  };

  const savePayoutDetails = async (e) => {
    e.preventDefault();
    setPayoutBusy(true);
    try {
      const data = await wsRequest("auth.updateMe", { payoutMethod, payoutDetails }, token);
      setProfile(data);
      announceAuthChange(data, token);
      showToast(payoutDetails.trim() ? "Saved." : "Removed.");
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setPayoutBusy(false);
  };

  if (!token) {
    return (
      <div style={{ color: "var(--text-primary)", minHeight: "100vh" }}>
        <div style={{ maxWidth: 480, margin: "80px auto", textAlign: "center", padding: "0 20px" }}>
        <h2>My account</h2>
        <p style={{ opacity: 0.8 }}>
          You need to be logged in to view this page. Go back to the{" "}
          <NavLink to="/" style={{ color: "var(--accent-blue)" }}>home page</NavLink> and log in.
        </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "account", label: "My Account" },
    { id: "security", label: "Security" },
    { id: "preferences", label: "My Competition Preferences" },
    { id: "payout", label: "Payment Details" },
    { id: "winnings", label: "My Winnings" },
    { id: "entries", label: "My Competitions" },
    { id: "payments", label: "My Payment History" },
    // CREDIT USAGE, not "top-up usage": this lists what credit was SPENT
    // on, which is the opposite direction from buying it. Top-ups appear
    // too, as the movements that paid for the rest.
    // ONLY WHEN ENTRY CREDIT IS ON. A tab offering a history of something
    // the site does not do is a trace of a feature that is switched off.
    // Reported 7 Sep.
    ...(credits?.enabled ? [{ id: "credits", label: "My Credit Balance" }] : []),
  ];

  return (
    <div style={{ color: "var(--text-primary)", minHeight: "100vh" }}>
      <div style={{ maxWidth: 600, margin: "0 auto", padding: "0 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2>My account</h2>
        <NavLink to="/" style={{ color: "var(--accent-blue)" }}>← Back to competition</NavLink>
      </div>

      <TabBar tabs={tabs} activeId={tab} onSelect={(id) => navigate(`/profile/${id}`)} />

      {tab === "account" &&
        (!form ? (
          <p>{error || "Loading…"}</p>
        ) : (
          <>
            {/* ENTRY CREDIT, above the details form. Shown whenever the
                feature is on, including at a zero balance — that is
                exactly when someone needs the top-up button. */}
            {credits && credits.enabled && (
              <section
                style={{
                  border: "1px solid var(--border-color)",
                  borderRadius: 8,
                  padding: 14,
                  marginBottom: 18,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <strong>Credit balance</strong>
                  <span style={{ color: "var(--accent-blue)", fontSize: 18 }}>
                    {(credits.credits || 0) +
                        (credits.otherModes || []).reduce((n, b) => n + (b.credits || 0), 0)}
                  </span>
                </div>
                {/* ONE NUMBER, AND NO MODE LANGUAGE.
                
                    This said "you also hold N credits in live mode", which is
                    operator vocabulary in a customer's account — and it appeared
                    because the page resolves a mode from the USER while the grant
                    uses the COMPETITION's, so a live purchase could read as
                    belonging somewhere else.
                
                    A real buyer only ever exists in one mode, so the total is the
                    honest figure to show them. The mismatch itself is recorded in
                    TODO.md rather than papered over. Reported 6 Sep. */}
                {/* GATED ON canTopUp, NOT enabled.

                    Since 1.261.0 `enabled` is always true — every site has
                    entry credit — and `canTopUp` carries the setting. Left
                    on `enabled` this would offer a button that the backend
                    refuses with a 409, which is the worst of both: the
                    buyer is invited to pay and then told no. */}
                {credits.canTopUp ? (
                  <TopUpControl symbol={symbol} token={token} demoRouteToken={demoRouteToken} credits={credits} />
                ) : (
                  <p style={{ fontSize: 13, opacity: 0.75, marginTop: 10, marginBottom: 0 }}>
                    Credit cannot be bought on this site. You earn it by buying pixels, and
                    spend it on entries beyond your free allowance.
                  </p>
                )}
              </section>
            )}
            <form onSubmit={save}>
            {(() => {
              const renderField = (f) => {
                const locked = f.key === "username" && profile.username;
                return (
                  <label key={f.key}>
                    {f.label}
                    <input
                      type={f.type}
                      required={f.key === "email"}
                      value={form[f.key] || ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      style={{ ...getInputStyle(theme), opacity: locked ? 0.6 : 1 }}
                      disabled={locked}
                    />
                    {locked && (
                      <div style={{ fontSize: 11, opacity: 0.55, marginTop: -10, marginBottom: 10 }}>
                        Username can't be changed once set.
                      </div>
                    )}
                  </label>
                );
              };
              return (
                <>
                  {renderField(FIELD_DEFS[0] /* username */)}

                  {phoneVerificationEnabled && (
                    <div
                      style={{
                        border: `1px solid ${profile?.phoneVerified && !phoneUnlocked ? "var(--accent-green)" : "var(--accent-yellow)"}`,
                        borderRadius: 8,
                        padding: 14,
                        marginBottom: 14,
                      }}
                    >
                      <strong style={{ display: "block", marginBottom: 6 }}>Phone number</strong>
                      <p style={{ margin: "0 0 10px", fontSize: 12.5, opacity: 0.8 }}>Required for payouts.</p>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <input
                          type="text"
                          placeholder="+14155551234"
                          value={phoneDraft}
                          disabled={profile?.phoneVerified && !phoneUnlocked}
                          onChange={(e) => setPhoneDraft(e.target.value)}
                          style={{ ...getInputStyle(theme), width: "auto", flex: "1 1 200px", marginBottom: 0 }}
                        />
                        {profile?.phoneVerified && !phoneUnlocked ? (
                          <button type="button" onClick={() => setPhoneUnlocked(true)}>
                            Reverify
                          </button>
                        ) : (
                          <button type="button" onClick={() => setShowPhoneModal(true)} disabled={!phoneDraft.trim()}>
                            Verify
                          </button>
                        )}
                        {phoneUnlocked && (
                          <button
                            type="button"
                            onClick={() => {
                              setPhoneUnlocked(false);
                              setPhoneDraft(profile?.phone || "");
                            }}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                      {profile?.phoneVerified && !phoneUnlocked && (
                        <p style={{ margin: "10px 0 0", fontSize: 13, color: "var(--accent-green)" }}>✓ Verified</p>
                      )}
                    </div>
                  )}

                  {FIELD_DEFS.slice(1).map(renderField)}
                </>
              );
            })()}

            {/* The spend-per-selection field lives in My Competition Preferences,
                not here.
            
                It was in both, writing the same defaultSpendAmount through two
                pieces of state — so changing it in one tab left the other showing
                the old value until a reload, and the explanatory text was
                duplicated almost word for word. Reported 5 Sep.
            
                Preferences is the right home: this is about a drawing tool on the
                homepage, not an account detail like a name or an address. */}

            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, opacity: 0.85 }}>
              <input
                type="checkbox"
                checked={!!form.emailUpdatesOptIn}
                onChange={(e) => setForm({ ...form, emailUpdatesOptIn: e.target.checked })}
              />
              Send me occasional site updates by email
            </label>

            {/* READ-ONLY ON PURPOSE. Un-agreeing and keeping an account is
                not a state this site has, so this records rather than
                asks. What it now records is checkable: the three
                documents themselves, when it was agreed, and — for an
                account a partner vouched for — WHOSE terms those were,
                because this site never obtained that agreement itself. */}
            <label data-legal-agreed style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 4, opacity: 0.85 }}>
              <input type="checkbox" checked={!!profile.legalAgreedAt} disabled readOnly style={{ marginTop: 3 }} />
              <span>
                I have read, understood and agree to the {legalLink("terms", "terms & conditions")},{" "}
                {legalLink("privacy", "privacy policy")} and {legalLink("disclaimers", "disclaimers")}
              </span>
            </label>
            <p data-legal-agreed-note style={{ fontSize: 12.5, opacity: 0.7, margin: "0 0 14px 26px" }}>
              {profile.legalAgreedAt
                ? profile.legalAgreedVia
                  ? `Agreed on ${new Date(profile.legalAgreedAt).toLocaleDateString()} through ${
                      profile.legalAgreedVia.domain || "the partner site you signed up on"
                    }, under their terms and privacy policy.`
                  : `Agreed on ${new Date(profile.legalAgreedAt).toLocaleDateString()} when you created your account.`
                : "No agreement is recorded on this account. Your account works as normal — the documents above are linked if you'd like to read them."}
            </p>

            {error && <p style={{ color: "var(--accent-red)" }}>{error}</p>}
            <button type="submit" disabled={busy}>
              {busy ? "Saving…" : "Save changes"}
            </button>
            </form>
          </>
        ))}

      {tab === "security" && (
        <section>
          <h3>Change password</h3>
          <form onSubmit={changePassword}>
            <label>
              Current password
              <input
                type="password"
                required
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                style={getInputStyle(theme)}
              />
            </label>
            <label>
              New password
              <input
                type="password"
                required
                minLength={8}
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                style={getInputStyle(theme)}
              />
            </label>
            <label>
              Confirm new password
              <input
                ref={confirmNewPasswordRef}
                type="password"
                required
                minLength={8}
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                style={getInputStyle(theme)}
              />
            </label>
            <button type="submit" disabled={passwordBusy}>
              {passwordBusy ? "Saving…" : "Change password"}
            </button>
          </form>

          <section style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid var(--border-color)" }}>
            <h3>Two-factor authentication</h3>
            {!profile ? (
              <p>Loading…</p>
            ) : settingUpTotp ? (
              <TotpSetup
                token={token}
                onCancel={() => setSettingUpTotp(false)}
                onComplete={() => {
                  setSettingUpTotp(false);
                  setProfile((prev) => (prev ? { ...prev, totpEnabled: true } : prev));
                }}
              />
            ) : profile.totpEnabled ? (
              <>
                <p style={{ fontSize: 13, opacity: 0.8 }}>
                  On — your account requires a code from your authenticator app to log in.
                </p>
                <form onSubmit={disableTotp} style={{ maxWidth: 360 }}>
                  <label>
                    Current password
                    <input
                      type="password"
                      required
                      value={disableTotpForm.password}
                      onChange={(e) => setDisableTotpForm({ ...disableTotpForm, password: e.target.value })}
                      style={getInputStyle(theme)}
                    />
                  </label>
                  <label>
                    Code from your app (or a backup code)
                    <input
                      type="text"
                      required
                      value={disableTotpForm.code}
                      onChange={(e) => setDisableTotpForm({ ...disableTotpForm, code: e.target.value })}
                      style={getInputStyle(theme)}
                    />
                  </label>
                  <button type="submit" disabled={disableTotpBusy}>
                    {disableTotpBusy ? "Turning off…" : "Turn off two-factor authentication"}
                  </button>
                </form>
              </>
            ) : (
              <>
                <p style={{ fontSize: 13, opacity: 0.8 }}>
                  Off — optional, adds a second step (a code from an authenticator app) when logging in, on top of
                  your password.
                </p>
                <button type="button" onClick={() => setSettingUpTotp(true)}>
                  Set up two-factor authentication
                </button>
              </>
            )}
          </section>
        </section>
      )}

      {tab === "preferences" && (
        <section>
          <h3>My Competition Preferences</h3>
          <p style={{ opacity: 0.8, fontSize: 14 }}>
            Set roughly how much you'd like to spend per selection — this powers the "Pick for me" and fixed-size
            drag tools on the homepage, sizing a selection to roughly match this amount at the active competition's
            current price per pixel.
          </p>
          <form onSubmit={savePreferences}>
            <label>
              Preferred spend per selection
              <input
                type="number"
                min="0"
                step="0.5"
                value={preferredSpend}
                onChange={(e) => setPreferredSpend(e.target.value)}
                style={getInputStyle(theme)}
              />
            </label>
            <button type="submit" disabled={prefsBusy}>
              {prefsBusy ? "Saving…" : "Save preference"}
            </button>
          </form>
        </section>
      )}

      {tab === "payout" && (
        <section>
          {phoneVerificationEnabled && (
            <div
              style={{
                border: `1px solid ${profile?.phoneVerified ? "var(--accent-green)" : "var(--accent-yellow)"}`,
                borderRadius: 8,
                padding: 14,
                marginBottom: 20,
              }}
            >
              <strong style={{ display: "block", marginBottom: 6 }}>Phone verification</strong>
              {profile?.phoneVerified ? (
                <p style={{ margin: 0, fontSize: 13.5, color: "var(--accent-green)" }}>
                  ✓ Verified{profile.phone ? ` — ${profile.phone}` : ""}.
                </p>
              ) : (
                <p style={{ margin: 0, fontSize: 13.5 }}>
                  Not verified yet — required before any prize winnings can be paid out. Head to the{" "}
                  <button
                    onClick={() => navigate("/profile/account")}
                    style={{ background: "none", border: "none", color: "var(--accent-yellow)", textDecoration: "underline", cursor: "pointer", padding: 0, font: "inherit" }}
                  >
                    My Account
                  </button>{" "}
                  tab to verify.
                </p>
              )}
            </div>
          )}
          <h3>Payment Details</h3>
          <p style={{ opacity: 0.8, fontSize: 14 }}>
            How you'd like to receive any prize money you win — set once here, used for every prize rather than
            asked again each time. Clear the details below and save to remove them entirely.
          </p>
          <form onSubmit={savePayoutDetails}>
            <div style={{ display: "flex", gap: 14, marginBottom: 10 }}>
              {[
                ["paypal", "PayPal"],
                ["bank_transfer", "Bank transfer"],
                ["other", "Other"],
              ].map(([value, label]) => (
                <label key={value} style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                  <input type="radio" checked={payoutMethod === value} onChange={() => setPayoutMethod(value)} />
                  {label}
                </label>
              ))}
            </div>
            <textarea
              value={payoutDetails}
              onChange={(e) => setPayoutDetails(e.target.value)}
              placeholder={
                payoutMethod === "paypal"
                  ? "Your PayPal email address"
                  : payoutMethod === "bank_transfer"
                  ? "Account name, sort code, account number"
                  : "Payment details"
              }
              rows={3}
              style={{
                width: "100%",
                padding: 8,
                background: "var(--bg-input)",
                border: "1px solid var(--border-color-light)",
                borderRadius: 6,
                color: "var(--text-primary)",
                boxSizing: "border-box",
                marginBottom: 8,
              }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button type="submit" disabled={payoutBusy}>
                {payoutBusy ? "Saving…" : payoutDetails.trim() ? "Save payment details" : "Remove payment details"}
              </button>
              {payoutDetails.trim() && (
                <button type="button" onClick={() => setPayoutDetails("")} disabled={payoutBusy}>
                  Clear
                </button>
              )}
            </div>
          </form>
        </section>
      )}

      {tab === "winnings" && (
        <section>
          <h3>My Winnings</h3>
          {winsError && <p style={{ color: "var(--accent-red)" }}>{winsError}</p>}
          {wins === null ? (
            <p>Loading…</p>
          ) : wins.length === 0 ? (
            <p style={{ opacity: 0.7 }}>You haven't won a competition yet.</p>
          ) : (
            wins.map((win) => (
              <div key={win.competitionId} style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 14, marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <strong>{win.title}</strong>
                  <span style={{ opacity: 0.6, fontSize: 13 }}>{siteDateTime(win.answeredAt)}</span>
                </div>
                <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
                  {win.wonByLottery ? `Won by lottery draw (ticket #${win.winningTicket})` : "Won by correct guess"}
                  {win.prizeMoney > 0 && (
                    <>
                      {" "}
                      — prize: <strong>{symbol}{win.prizeMoney.toFixed(2)}</strong>
                    </>
                  )}
                </div>

                {win.prizeMoney <= 0 ? (
                  <p style={{ fontSize: 13, opacity: 0.6, marginTop: 10, marginBottom: 0 }}>
                    This competition had no prize money attached.
                  </p>
                ) : win.prizePaid ? (
                  <div style={{ marginTop: 10, fontSize: 13, color: "var(--accent-green)" }}>
                    Paid{win.paidAt ? ` on ${siteDate(win.paidAt)}` : ""}
                    {win.paidVia ? ` via ${win.paidVia}` : ""}.
                  </div>
                ) : phoneVerificationEnabled && !profile?.phoneVerified ? (
                  <div style={{ marginTop: 10, fontSize: 13, color: "var(--accent-yellow)" }}>
                    Congratulations! Verify your phone before this prize can be paid —{" "}
                    <button
                      onClick={() => setShowPhoneModal(true)}
                      style={{ background: "none", border: "none", color: "var(--accent-yellow)", textDecoration: "underline", cursor: "pointer", padding: 0, font: "inherit" }}
                    >
                      verify now
                    </button>
                    .
                  </div>
                ) : win.payoutRequestedAt && (!(profile?.payoutMethod && profile?.payoutDetails) || !isDeliveryProfileComplete(profile)) ? (
                  <div style={{ marginTop: 10, fontSize: 13, color: "var(--accent-yellow)" }}>
                    {!(profile?.payoutMethod && profile?.payoutDetails) && (
                      <div>
                        An admin is waiting on your payment details for this prize — head to the{" "}
                        <button
                          onClick={() => navigate("/profile/payout")}
                          style={{ background: "none", border: "none", color: "var(--accent-yellow)", textDecoration: "underline", cursor: "pointer", padding: 0, font: "inherit" }}
                        >
                          Payment Details
                        </button>{" "}
                        tab to set them up.
                      </div>
                    )}
                    {!isDeliveryProfileComplete(profile) && (
                      <div style={{ marginTop: !(profile?.payoutMethod && profile?.payoutDetails) ? 6 : 0 }}>
                        Your delivery details aren't complete yet — head to the{" "}
                        <button
                          onClick={() => navigate("/profile/account")}
                          style={{ background: "none", border: "none", color: "var(--accent-yellow)", textDecoration: "underline", cursor: "pointer", padding: 0, font: "inherit" }}
                        >
                          My Account
                        </button>{" "}
                        tab to fill them in.
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ marginTop: 10, fontSize: 13, opacity: 0.7 }}>Awaiting payment from an admin.</div>
                )}
              </div>
            ))
          )}
        </section>
      )}

      {replayId && (
        <CompetitionReplayModal
          competitionId={replayId}
          token={token}
          onClose={() => setReplayId(null)}
          loadData={() =>
            fetch(`/api/v1/auth/my-replays/${replayId}`, { headers: { "x-auth-token": token } })
              .then((res) => {
                if (!res.ok) throw new Error("This replay is no longer available.");
                return res.json();
              })
              // The modal wants one competition and the two display
              // values; the snapshot stores exactly that, under its own
              // names.
              .then((d) => ({
                competition: d.competition || d.competitions?.[0],
                currencySymbol: d.currencySymbol,
                creditsPerMajorUnitOnPixels: d.creditsPerMajorUnitOnPixels,
                // WHICH OF THOSE PURCHASES AND GUESSES WERE MINE (1.398.0).
                // Indexes, worked out by the server from ids it never
                // sends — this is a replay of a competition this person
                // played in, so it shows it from where they stood.
                you: d.you || null,
              }))
          }
          progressiveImageUrl={(regionCount) =>
            `/api/v1/auth/my-replays/${replayId}/image?regionCount=${regionCount}`
          }
          revealedImageUrl={`/api/v1/auth/my-replays/${replayId}/image?revealed=1`}
        />
      )}

      {tab === "entries" && (
        <section>
          {entriesError && <p style={{ color: "var(--accent-red)" }}>{entriesError}</p>}
          {entries === null && !entriesError ? (
            <p>Loading…</p>
          ) : entries && entries.length === 0 ? (
            <p style={{ opacity: 0.7 }}>You haven't entered any competitions yet.</p>
          ) : (
            entries &&
            entries.map((e) => (
              <div
                key={e.id}
                style={{
                  border: "1px solid var(--border-color)",
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 10,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                <div>
                  <strong>{e.competitionTitle}</strong>
                  <div style={{ fontSize: 13, opacity: 0.7 }}>
                    {e.guess === null
                      ? `Won via lottery draw (no guess submitted) · ${siteDateTime(e.createdAt)}`
                      : `Guessed "${e.guess}" · ${siteDateTime(e.createdAt)} · ${entryFunding(e)}`}
                  </div>
                </div>
                {/* THE BADGE IS THE BUTTON. The whole row used to be
                    clickable, which gave no sign that anything would
                    happen and made the verdict look like decoration. */}
                <button
                  type="button"
                  onClick={() => openCompetitionDetail(e.competitionId)}
                  title="See your guesses for this competition"
                  style={{
                    color: e.guess === null ? "var(--accent-yellow)" : e.correct ? "var(--accent-green)" : "var(--accent-red)",
                    border: `1px solid ${e.guess === null ? "var(--accent-yellow)" : e.correct ? "var(--accent-green)" : "var(--accent-red)"}`,
                    background: "none",
                    borderRadius: 4,
                    padding: "2px 8px",
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  {e.guess === null ? "🏆 Won" : e.correct ? "Correct" : "Incorrect"}
                </button>

                {/* Only where there is something to play. */}
                {replayable.has(e.competitionId) && (
                  <button
                    type="button"
                    onClick={() => setReplayId(e.competitionId)}
                    title="Watch this competition play out again"
                    style={{ fontSize: 13, padding: "2px 10px", cursor: "pointer" }}
                  >
                    ▶ Replay
                  </button>
                )}
              </div>
            ))
          )}
        </section>
      )}

      {detailCompetitionId && (
        <div
          onClick={() => setDetailCompetitionId(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--bg-panel)",
              border: "1px solid var(--border-color)",
              borderRadius: 10,
              padding: 24,
              maxWidth: 480,
              width: "90%",
              maxHeight: "80vh",
              overflowY: "auto",
              color: "var(--text-primary)",
            }}
          >
            {detailError ? (
              <p style={{ color: "var(--accent-red)" }}>{detailError}</p>
            ) : !detail ? (
              <p>Loading…</p>
            ) : (
              <>
                <h3 style={{ marginTop: 0 }}>{detail.title}</h3>
                {detail.prizeMoney > 0 && (
                  <p style={{ opacity: 0.8, fontSize: 14 }}>
                    Prize: <strong>{symbol}{detail.prizeMoney.toFixed(2)}</strong>
                  </p>
                )}

                {!detail.isAnswered ? (
                  <p style={{ opacity: 0.7, fontSize: 14 }}>Still running — no result yet.</p>
                ) : (
                  <p style={{ fontSize: 14 }}>
                    The answer was <strong>{detail.answer}</strong>.{" "}
                    {detail.wonByLottery ? (
                      <>
                        Solved by lottery draw
                        {detail.winnerUsername && (
                          <>
                            {" "}
                            — won by <strong>{detail.winnerUsername}</strong>
                          </>
                        )}
                        .
                      </>
                    ) : (
                      detail.winnerUsername && (
                        <>
                          Correctly guessed by <strong>{detail.winnerUsername}</strong>.
                        </>
                      )
                    )}
                  </p>
                )}

                {detail.wonByLottery && (
                  <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, marginTop: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Your tickets</div>
                    {!detail.myTicketRanges || detail.myTicketRanges.length === 0 ? (
                      <p style={{ fontSize: 13, opacity: 0.6, margin: 0 }}>
                        You didn't hold any tickets in this draw (no account-bound purchases).
                      </p>
                    ) : (
                      <>
                        {detail.myTicketRanges.map((r, i) => {
                          const isWinningRange =
                            detail.winningTicket != null && detail.winningTicket >= r.start && detail.winningTicket <= r.end;
                          return (
                            <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
                              {r.start === r.end ? `Ticket #${r.start}` : `Tickets #${r.start}–#${r.end}`}{" "}
                              <span style={{ opacity: 0.6 }}>({r.count} ticket{r.count === 1 ? "" : "s"})</span>
                              {isWinningRange && (
                                <span style={{ color: "var(--accent-yellow)", fontWeight: 600 }}>
                                  {" "}
                                  — 🏆 includes ticket #{detail.winningTicket}, the winning ticket!
                                </span>
                              )}
                            </div>
                          );
                        })}
                        {detail.myTicketsIncludeWinner && (
                          <p style={{ color: "var(--accent-yellow)", fontWeight: 600, marginTop: 8, marginBottom: 0 }}>
                            🎉 Ticket #{detail.winningTicket} was yours — you won!
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Your guesses</div>
                  {detail.myGuesses.length === 0 ? (
                    <p style={{ fontSize: 13, opacity: 0.6, margin: 0 }}>
                      You didn't submit any guesses — this win came from purchased pixels alone.
                    </p>
                  ) : (
                    detail.myGuesses.map((g, i) => (
                      <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
                        "{g.guess}" —{" "}
                        <span style={{ color: g.correct ? "var(--accent-green)" : "var(--accent-red)" }}>{g.correct ? "Correct" : "Incorrect"}</span>
                        {" · "}
                        <span style={{ opacity: 0.6 }}>{siteDateTime(g.createdAt)}</span>
                      </div>
                    ))
                  )}
                </div>

                <button onClick={() => setDetailCompetitionId(null)} style={{ marginTop: 16 }}>
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {tab === "credits" && (
        <section>
          {/* WHERE THE CREDIT WENT.
              Every movement, newest first, with the balance either side.
              The balance BEFORE is derived from the one after and the
              movement itself, so the two can never disagree. */}
          {/* NOT "entry credit is not in use on this site" any more. That
              read off `enabled`, which was false whenever the setting was
              off — on a site where credit was still being earned and spent.
              `enabled` is always true since 1.261.0, so the only reason to
              have nothing here is a failed read. */}
          {!credits ? (
            <p style={{ opacity: 0.7 }}>Could not load your credit history.</p>
          ) : (credits.recent || []).length === 0 ? (
            <p style={{ opacity: 0.7 }}>
              Nothing yet. Credit appears here as soon as you earn or spend some.
            </p>
          ) : (
            <div>
              <p style={{ fontSize: 13, opacity: 0.75, marginTop: 0 }}>
                Every movement on your balance, newest first.
              </p>
              {(credits.recent || []).map((e, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 12,
                    padding: "10px 0",
                    borderBottom: "1px solid var(--border-color-subtle)",
                  }}
                >
                  <span style={{ fontSize: 13 }}>
                    <strong>
                      {/* A PIXEL IS NOT A BALANCE MOVEMENT. A free pixel
                          is recorded with credits: 0 and pixels: 1, so
                          it reads as what it is rather than as a credit
                          change of nothing. */}
                      {e.type === "pixel"
                        ? `${e.pixels || 1} pixel${(e.pixels || 1) === 1 ? "" : "s"}`
                        : e.competitionTitle
                          ? e.competitionTitle
                          : e.type === "expire"
                            ? "Free credit expired"
                            : e.type === "credit"
                              ? "Credit added"
                              : "Credit used"}
                    </strong>
                    {/* MARKED FREE, so a dispute can be answered from
                        this screen rather than from the competition
                        data. Everything free the account was ever given
                        appears here: the grant, the spend, the pixel,
                        and the expiry of anything unused. */}
                    {e.free && (
                      <span
                        style={{
                          marginLeft: 6,
                          fontSize: 11.5,
                          padding: "1px 6px",
                          borderRadius: 10,
                          border: "1px solid var(--accent-green, #5ed46a)",
                          color: "var(--accent-green, #5ed46a)",
                        }}
                      >
                        Free
                      </span>
                    )}
                    {/* A FREE SPEND SAYS SO IN WORDS, not only by badge.
                        The reason a spend carries is "entry" whether the
                        credit was free or bought, so the row read
                        "#160566 — entry, -5" either way and
                        the badge alone left it to be inferred.

                        That is exactly the case this exists for: in a
                        dispute a buyer could say they were granted
                        credits and never used them, and the history has
                        to be able to contradict it. */}
                    {e.reason && (
                      <span style={{ opacity: 0.65 }}>
                        {" "}
                        — {e.reason}
                        {e.free && e.type === "spend" ? " (paid with free credit)" : ""}
                      </span>
                    )}
                    <br />
                    <span style={{ opacity: 0.65, fontSize: 12 }}>
                      {siteDateTime(e.at)}
                    </span>
                  </span>
                  <span style={{ textAlign: "right", whiteSpace: "nowrap", fontSize: 13 }}>
                    <strong
                      style={{
                        color:
                          e.type === "pixel"
                            ? "var(--text-primary)"
                            : (e.credits || 0) > 0
                              ? "var(--accent-green)"
                              : "var(--accent-red)",
                      }}
                    >
                      {/* A PIXEL ROW CARRIES NO CREDIT, so showing its
                          amount would print a red zero. It shows the
                          thing that actually moved instead. */}
                      {e.type === "pixel"
                        ? `${e.pixels || 1} px`
                        : `${(e.credits || 0) > 0 ? "+" : ""}${e.credits}`}
                    </strong>
                    {e.balanceAfter != null && (
                      <>
                        <br />
                        <span style={{ opacity: 0.65, fontSize: 12 }}>
                          {e.balanceBefore} → {e.balanceAfter}
                        </span>
                      </>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === "payments" && (
        <section>
          {paymentsError && <p style={{ color: "var(--accent-red)" }}>{paymentsError}</p>}
          {paymentGroups === null && !paymentsError ? (
            <p>Loading…</p>
          ) : paymentGroups && paymentGroups.length === 0 ? (
            <p style={{ opacity: 0.7 }}>You haven't spent anything yet.</p>
          ) : (
            paymentGroups &&
            paymentGroups.map((g) => (
              <div key={g.competitionId || "entry-credit"} style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <strong>{g.competitionTitle}</strong>
                  <span style={{ color: "var(--accent-blue)" }}>{symbol}{(g.totalSpent || 0).toFixed(2)} total</span>
                </div>
                <div style={{ marginTop: 10 }}>
                  {g.payments.map((p) => (
                    <div
                      key={p.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 13,
                        opacity: 0.8,
                        padding: "4px 0",
                        borderTop: "1px solid var(--border-color-subtle)",
                      }}
                    >
                      <span>
                        {p.type === "pixel_purchase"
                          ? "Image area"
                          : p.type === "credit_topup"
                          ? "Entry credit top-up"
                          : "Guess entry"}{" "}
                        {/* NEVER RENDER details RAW. It is free-form text
                            written by whichever backend path recorded the
                            payment, and one of them passed an OBJECT —
                            React error #31, which took this whole page
                            down for anyone who had topped up. The backend
                            is fixed, but a payment history that cannot be
                            opened is a bad way to find out about the next
                            one, and old records keep the old shape. */}
                        {typeof p.details === "string" && p.details ? "— " + p.details + " " : ""}·{" "}
                        {siteDateTime(p.createdAt)}
                      </span>
                      <span>
                          {symbol}
                          {(p.amount || 0).toFixed(2)}
                          {/* WHAT IT EARNED. Recorded on the payment at the time, so
                              this cannot drift when a rate changes. Absent on
                              anything bought before credit was granted at all. */}
                          {p.creditsEarned > 0 && (
                            <span style={{ opacity: 0.75, fontSize: 12 }}>
                              {" "}
                              · earned {p.creditsEarned} credit
                              {p.creditsEarned === 1 ? "" : "s"}
                            </span>
                          )}
                          {/* WHAT A TOP-UP BOUGHT. Same reasoning as
                              above — recorded at the moment it was
                              granted, so it cannot drift when a rate
                              changes. The history showed the amount paid
                              and nothing about credits at all. */}
                          {p.creditsGranted > 0 && (
                            <span style={{ opacity: 0.75, fontSize: 12 }}>
                              {" "}
                              · {p.creditsGranted} credit
                              {p.creditsGranted === 1 ? "" : "s"}
                            </span>
                          )}
                        </span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </section>
      )}
      </div>
      {showPhoneModal && (
        <PhoneVerificationModal
          token={token}
          initialPhone={phoneDraft.trim() || undefined}
          onVerified={() => {
            setShowPhoneModal(false);
            refreshProfile();
            showToast("Phone verified.", "success");
          }}
          onClose={() => setShowPhoneModal(false)}
        />
      )}
      <Footer />
    </div>
  );
}


// Buying entry credit.
//
// REST, not the WebSocket used everywhere else on this page. The top-up
// route creates a pending purchase and returns a provider redirect URL;
// that is the same shape as the pixel-purchase checkout, which is also
// REST, and matching it keeps one payment flow rather than two.
//
// The buyer LEAVES THIS PAGE on success — window.location goes to the
// provider's hosted page. Nothing is granted here; the balance changes
// only when the webhook or poll confirms the payment, which may be
// minutes later and on a completely separate request.
function TopUpControl({ symbol, token, demoRouteToken = null, credits = null }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const start = async () => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setError("Enter an amount.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // THE ROUTE TOKEN DECIDES WHICH PAYMENT CREDENTIALS ARE USED.
      // Without it the backend's requestMode returns "live" and asks for
      // live credentials — which on a test site are not configured, so a
      // top-up in test mode failed with "Cashflows isn't fully configured
      // for live". The account page has no token of its own; it comes from
      // the tab's preview session via main.jsx.
      const res = await fetch("/api/v1/credits/topup/start", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-auth-token": token },
        body: JSON.stringify({ amount: value, demoRouteToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // The server's own message says WHY — below the minimum, above the
        // ceiling, credits switched off. Showing it beats a generic
        // failure, and each of those is something the buyer can act on.
        setError(data.error || "Could not start checkout.");
        setBusy(false);
        return;
      }
      if (!data.redirectUrl) {
        setError("The payment provider did not return a checkout link.");
        setBusy(false);
        return;
      }
      window.location.href = data.redirectUrl;
    } catch {
      setError("Could not reach the server.");
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 10 }}>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)}>
          Top up
        </button>
      ) : (
        <div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ opacity: 0.7 }}>{symbol}</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Amount"
              style={{ width: 110, padding: 6 }}
            />
            <button type="button" onClick={start} disabled={busy}>
              {busy ? "Starting…" : "Continue to payment"}
            </button>
            <button type="button" onClick={() => { setOpen(false); setError(null); }} disabled={busy}>
              Cancel
            </button>
          </div>
          {/* THE EXCHANGE RATE, WHERE THE MONEY IS BEING TYPED.

              A buyer entering an amount needs to know what it buys. The rate

              can change, so it is read from the server rather than assumed,

              and the minimum is stated in the same breath because it is the

              other thing that will stop the payment. */}

          {credits?.creditsPerMajorUnit > 0 && (

            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>

              {/* WHAT THIS AMOUNT ACTUALLY BUYS, worked out for them.
                  The rate was on screen and the arithmetic was the
                  buyer's problem — and this is the number they are
                  agreeing to pay for.

                  Math.floor, matching creditsForMoney on the server, so
                  the figure here is the figure granted rather than a
                  rounded-up promise. */}
              {Number(amount) > 0 && credits.creditsPerMajorUnit > 0 && (
                <div style={{ marginBottom: 4 }}>
                  {symbol}{Number(amount).toFixed(2)} buys{" "}
                  <strong>
                    {Math.floor(Number(amount) * credits.creditsPerMajorUnit)} credits
                  </strong>
                </div>
              )}
              {symbol}1 buys <strong>{credits.creditsPerMajorUnit} credits</strong>

              {/* WITH WHAT IT COSTS. The admin card has shown the money
                    equivalent since the setting existed; the buyer saw only
                    a credit count and found the price at the payment page.
                    Rounded UP, the same direction moneyForCredits rounds on
                    the server, so the figure shown is never less than the
                    amount that will be charged. Requested 6 Sep. */}
                {credits.minCreditTopUpCredits > 0 &&
                  ` · smallest top-up ${credits.minCreditTopUpCredits} credits` +
                    (credits.creditsPerMajorUnit > 0
                      ? ` (${symbol}${(
                          Math.ceil(
                            (credits.minCreditTopUpCredits / credits.creditsPerMajorUnit) * 100
                          ) / 100
                        ).toFixed(2)})`
                      : "")}

              {credits.creditsPerMajorUnitOnPixels > credits.creditsPerMajorUnit && (

                <>

                  <br />

                  Buying pixels earns more: {credits.creditsPerMajorUnitOnPixels} credits per {symbol}1

                </>

              )}

            </div>

          )}

          <div style={{ fontSize: 11, opacity: 0.55, marginTop: 6 }}>
            You'll be taken to our payment provider. Your balance updates once the payment is
            confirmed, which can take a moment.
          </div>
        </div>
      )}
      {error && (
        <div style={{ fontSize: 12, color: "var(--accent-red)", marginTop: 6 }}>{error}</div>
      )}
    </div>
  );
}
